import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { CustomValidator } from '../../validators/custom.validators';
import { ERRORS, Constants } from '../../models/constants';
import { throwError } from 'rxjs';
import { IValidateDateTime, ValidationsSchema, DateValidationTypes } from '../../models/interfaces';
import { CoreCommonService } from './common.service';
// @dynamic
@Injectable({
  providedIn: 'root'
})

export class CoreDynamicFormService {
  constructor(private customValidator: CustomValidator, private coreCommonService: CoreCommonService) { }

  // TODO: Reduce code complexity
  /**
   * This method will convert controls to form group
   * @param controls - controls
   */
  async toFormGroup(controls: Array<any> = [], ...scopes: any ) {
    const group: FormGroup = new FormGroup({});
    // const controlsCopy = JSON.parse(JSON.stringify(controls));
    for (const ctrl of controls) {

      for (const field of ctrl['fields']) {
        if (field.fieldType === Constants.controls.Button || field.fieldType === Constants.controls.Horizontal_Rule) { continue; }
        const validatorsList: Array<any> = await this.getValidatorsList(field) || [];
        if (field.hasOwnProperty('isNested') && field.isNested) {
          const subFormGroup = new FormGroup({});
          for (const subField of field.subForm.fields) {
            const subControlValidatorsList: Array<any> = await this.getValidatorsList(subField) || [];
            const defaultValue = await this.coreCommonService.fetchDefaultValue(subField.defaultValueLookup, subFormGroup);
            subFormGroup.addControl(subField.logicalName, new FormControl({
              value: defaultValue || null, disabled: subField.readOnly
            }, subControlValidatorsList));
          }
          // group[field.logicalName] = subFormGroup as FormGroup;
          group.addControl(field.logicalName, subFormGroup);
          this.addFormLevelValidators(group, field);
        } else {
          // group[field.logicalName] = new FormControl({ value: field.defaultValueLookup
          //  || null, disabled: field.readOnly }, validatorsList);
          const defaultValue = await this.coreCommonService.fetchDefaultValue(field.defaultValueLookup, group, ...scopes);
          const control: FormControl = new FormControl({
            value: defaultValue || null, disabled: field.readOnly
          }, validatorsList);
          // group[field.logicalName] = control;
          group.addControl(field.logicalName, control);
          this.addFormLevelValidators(group, field, control);
        }
      }

    }
    // const formLevelValidators = await this.getFormLevelValidators(formGroup);
    // return new FormGroup(group, { validators: minDate('passportIssueDate', 'passportExpiryDate') });
    return group;
  }

  // INFO: Moved this fn to common.services.ts
  // getApiEndPoint(moduleObj: object, apiName: string): object {
  // }

  // ---------------------------------------------- Private Functions -------------------------------

  // TODO: Reduce code complexity
  /**
   * get validators list
   * @param fieldObj - field details
   */
  private getValidatorsList(fieldObj): any {
    const validations = [];
    // TODO: Move all these calls to parallel exec
    const regex = this.regexCheck(fieldObj);
    const mandatory = this.mandatoryCheck(fieldObj);
    const minLength = this.minLengthCheck(fieldObj);
    const maxLength = this.maxLengthCheck(fieldObj);
    const minValCheck = this.minValueCheck(fieldObj);
    const maxValCheck = this.maxValueCheck(fieldObj);

    validations.push(...regex, ...mandatory, ...minLength, ...maxLength, ...minValCheck, ...maxValCheck);
    return validations.length > 1 ? Validators.compose(validations) : validations;
  }

  /**
   * This method will check the regex of field value
   * @param field - field details
   */
  private regexCheck(field: object): Array<any> {
    try {
      const validations = [];
      if (!field || !field['validations']) { return validations; }
      const regex: RegExp = field['validations'].regex ? field['validations'].regex.value : undefined;
      if (regex) { validations.push(Validators.pattern(regex)); }
      return validations;
    } catch (error) { console.log(error); }
  }

  /**
   * This method will check for mandatory flag
   * @param field - field
   */
  private mandatoryCheck(field: object): Array<any> {
    const validations = [];
    if (!field || !field['validations'] || !field['validations'].hasOwnProperty('mandatory')) { return validations; }
    if (field['validations'].mandatory && field['validations'].mandatory.value) {
      validations.push(field['fieldType'] === 'checkbox' ? Validators.requiredTrue
        : Validators.required);
    }
    return validations;
  }

  /**
   * This method will check for min length
   * @param field - field
   */
  private minLengthCheck(field: object): Array<any> {
    const validations = [];
    if (!field || !field['validations'] || !field['validations'].hasOwnProperty('minLength')) { return validations; }
    if (field['validations'].minLength) { validations.push(Validators.minLength(field['validations'].minLength.value)); }
    return validations;
  }

  /**
   * This method will check for max length
   * @param field - field
   */
  private maxLengthCheck(field: object): Array<any> {
    const validations = [];
    if (!field || !field['validations'] || !field['validations'].hasOwnProperty('maxLength')) { return validations; }
    if (field['validations'].maxLength) { validations.push(Validators.maxLength(field['validations'].maxLength.value)); }
    return validations;
  }

  /**
   * This method will check for min value
   * @param field - field
   */
  private minValueCheck(field: object): Array<any> {
    const validations = [];
    if (!field || !field['validations'] || !field['validations'].hasOwnProperty('minValue')) { return validations; }
    if (field['fieldType'] !== 'datepicker' && field['validations'].minValue) {
      // if (field['validations'].minValue) { validations.push(minDate(field['validations'].minValue.value)); }
      validations.push(Validators.min(field['validations'].minValue.value));
    }
    return validations;
  }

  /**
   * This method will check for max value
   * @param field - field
   */
  private maxValueCheck(field: object): Array<any> {
    const validations = [];
    if (!field || !field['validations'] || !field['validations'].hasOwnProperty('maxValue')) { return validations; }
    if (field['fieldType'] !== Constants.controls.Datepicker && field['validations'].maxValue) {
      validations.push(Validators.max(field['validations'].maxValue.value));
    }
    return validations;
  }

  /**
   * This method will add form level validators
   */
  private addFormLevelValidators(formGroup: FormGroup, field: any, control?) {
    if (field.fieldType === Constants.controls.Datepicker) {
      this.setMinDateValidator(formGroup, field, control);
      this.setMaxDateValidator(formGroup, field, control);
    } else if (field.fieldType === Constants.controls.Timepicker) {
      this.dateTimeValidator(formGroup, field);
    }
  }

  // ------------------------------- Datetime validator --------------------------

  /**
   * This method will validate data and time
   * @param formGroup - form group
   * @param field - field
   */
  private dateTimeValidator(formGroup: FormGroup, field: object) {
    try {

      const supportingParams: IValidateDateTime = field['validations'].validateDateTime;
      this.setDateTimePickerValidator(formGroup, field, supportingParams);
    } catch (error) { console.log(ERRORS.DATETIME_VALIDATOR_FAILED); }
    return true;
  }

  /**
   * This method will set validator for date time
   * @param formGroup - form group
   * @param field - field
   * @param supportingParams - params
   */
  private setDateTimePickerValidator(formGroup: FormGroup, field: object, supportingParams: IValidateDateTime) {
    try {

      if (supportingParams.comparisonType === 'POST-TIME') {
        formGroup.setValidators(Validators.compose([formGroup.validator, this.customValidator.dateTimeValidator(field, supportingParams)]));
      }
    } catch (error) { throwError(error); }
  }

  // -----------------------------------------------------------------------------
  /**
   * This method will set min date in date picker
   */
  private setMinDateValidator(formGroup: FormGroup, field: object, control?): boolean {
    try {
      // const value = field['validations'].minValue.value;
      // if (value) { this.setDatepickerValidators(value, 'MIN', formGroup, field['logicalName']); }
      const minValue: ValidationsSchema = field['validations'].minValue;
      if (!minValue || !minValue.isActive) { return; }
      const existingValidators = control.validator;
      switch (minValue.type) {
        case DateValidationTypes.CURRENT:
          // formGroup.setValidators(Validators.compose([existingValidators, this.customValidator.minCurrentDate(field['logicalName'])]));
          control.setValidators(Validators.compose([existingValidators, this.customValidator.minDate(field['logicalName'], minValue)]));
          break;

        case DateValidationTypes.COMPARE:
          control.setValidators(Validators.compose([existingValidators,
            this.customValidator.lessThanTargetDate(formGroup, minValue)]));
          break;

        case DateValidationTypes.LATEST_AMONG:
          control.setValidators(Validators.compose([existingValidators, this.customValidator.latestAmong(formGroup, field)]));
          break;

        default:
          break;
      }
    } catch (error) { console.log(ERRORS.MIN_DATE_VALIDATOR_FAILED); }
    return true;
  }

  // private setMaxDateValidator(formGroup: FormGroup, field: any): boolean {
  //   try {
  //     const value = field['validations'].maxValue.value;
  //     if (value) { this.setDatepickerValidators(value, 'MAX', formGroup, field['logicalName']); }
  //   } catch (error) { console.log(ERRORS.MAX_DATE_VALIDATOR_FAILED); }
  //   return true;
  // }

  // private setDatepickerValidators(type: DateValidationTypes, boundary: string, formGroup: FormGroup, fieldName: string) {
  //   try {
  //     const existingValidators = formGroup.validator;
  //     switch (type) {
  //       case VALIDATIONS.datepicker.MAX_CURRENT:
  //         formGroup.setValidators(Validators.compose([existingValidators, this.customValidator.maxCurrentDate(fieldName)]));
  //         break;
  //       case VALIDATIONS.datepicker.MIN_CURRENT:
  //         formGroup.setValidators(Validators.compose([existingValidators, this.customValidator.minCurrentDate(fieldName)]));
  //         break;
  //       default:
  //         if (boundary === 'MIN') {
  //           formGroup.setValidators(Validators.compose([existingValidators, this.customValidator.minDate(fieldName, type)]));
  //         } else if (boundary === 'MAX') {
  //           formGroup.setValidators(Validators.compose([existingValidators,
  //             this.customValidator.maxDate(fieldName, type)]));
  //         }
  //         break;
  //     }
  //     formGroup.updateValueAndValidity();
  //   } catch (error) { throwError(error); }
  // }

  /**
   * This method will set max date for datepicker
   */
  private setMaxDateValidator(formGroup: FormGroup, field: object, control?) {
    try {
      const maxValue: ValidationsSchema = field['validations'].maxValue;
      if (!maxValue || !maxValue.isActive) { return; }
      const existingValidators = control.validator;
      switch (maxValue.type) {
        case DateValidationTypes.CURRENT:
          control.setValidators(Validators.compose([existingValidators,
            this.customValidator.maxDate(field['logicalName'], maxValue)]));
          break;
        case DateValidationTypes.COMPARE:
          control.setValidators(Validators.compose([existingValidators, this.customValidator.
            greaterThanTargetDate(formGroup, maxValue['value'])]));
          break;
        case DateValidationTypes.LATEST_AMONG:
          control.setValidators(Validators.compose([existingValidators, this.customValidator.latestAmongMax(formGroup, field)]));
          break;
        case DateValidationTypes.SESSION:
          control.setValidators(Validators.compose([existingValidators, this.customValidator.maxDateFromSession(formGroup, field)]));
          break;
        default:
          break;
      }
      control.updateValueAndValidity();
    } catch (error) { throwError(error); }
  }


}
